// ═══════════════════════════════════════════════════════════════
//  天赋系统
//  - 20 种天赋，每种可叠加，数值上限 100%
//  - 玩家天赋槽随层数解锁：3 槽 → 8 槽（每 20 层 +1）
//  - 每次【进门】触发一次天赋选择
//  - 文案全部走 i18n（nameKey / descKey），按语言渲染
// ═══════════════════════════════════════════════════════════════
import { tr } from './i18n';
import * as Bal from './balance';

/** 单个天赋的叠加值上限（%） */
export const TALENT_MAX = 100;
/** 基础天赋槽数（第 1 层） */
export const TALENT_SLOTS_BASE = 3;
/** 获取当前层数的天赋槽数（动态） */
export function getTalentSlots(floor: number): number {
  return Bal.talentSlotsForFloor(floor);
}

/** 天赋能修改的玩家属性（结构化接口，避免与 Player 循环依赖） */
export interface TalentTarget {
  damageMul: number;
  fireRate: number;
  moveSpeed: number;
  maxHp: number;
  bulletSpeed: number;
  extraBullets: number;
  pierce: number;
  critChance: number;
  critMul: number;
  lifesteal: number;
  healPerHit: number;
  regenPerFloor: number;
  thorns: number;
  damageReduction: number;
  dropMul: number;
  scoreMul: number;
  pickupRadius: number;
  knockbackResist: number;
  eliteDmgMul: number;
  slowAmount: number;
}

export interface TalentDef {
  id: string;
  /** i18n 键：名字 */
  nameKey: string;
  /** i18n 键：描述模板（占位符 {v}/{p}/{n}/{extra}） */
  descKey: string;
  /** 每次选择该天赋增加的百分比（叠加步长） */
  step: number;
  /** 把百分值应用到玩家属性上（在已重置为基准值之后调用） */
  apply: (p: TalentTarget, v: number) => void;
  /** 描述模板需要的额外占位符参数 */
  params?: (v: number) => Record<string, number | string>;
}

export const TALENTS: TalentDef[] = [
  {
    id: 'dmg', nameKey: 'talent.dmg.name', descKey: 'talent.dmg.desc', step: 20,
    apply: (p, v) => { p.damageMul += v / 100; },
  },
  {
    id: 'rate', nameKey: 'talent.rate.name', descKey: 'talent.rate.desc', step: 20,
    apply: (p, v) => { p.fireRate *= 1 - 0.6 * v / 100; },
    params: (v) => ({ p: Math.round(v * 0.6) }),
  },
  {
    id: 'speed', nameKey: 'talent.speed.name', descKey: 'talent.speed.desc', step: 20,
    apply: (p, v) => { p.moveSpeed *= 1 + v / 100; },
  },
  {
    id: 'hp', nameKey: 'talent.hp.name', descKey: 'talent.hp.desc', step: 25,
    apply: (p, v) => { p.maxHp += v; },
  },
  {
    id: 'bullet', nameKey: 'talent.bullet.name', descKey: 'talent.bullet.desc', step: 20,
    apply: (p, v) => { p.bulletSpeed *= 1 + v / 100; },
  },
  {
    id: 'multi', nameKey: 'talent.multi.name', descKey: 'talent.multi.desc', step: 25,
    apply: (p, v) => { p.extraBullets += Math.round(v / 100 * 4); },
    params: (v) => ({ extra: Math.round(v / 100 * 4) }),
  },
  {
    id: 'pierce', nameKey: 'talent.pierce.name', descKey: 'talent.pierce.desc', step: 34,
    apply: (p, v) => { p.pierce += Math.round(v / 100 * 3); },
    params: (v) => ({ extra: Math.round(v / 100 * 3) }),
  },
  {
    id: 'crit', nameKey: 'talent.crit.name', descKey: 'talent.crit.desc', step: 20,
    apply: (p, v) => { p.critChance += 0.6 * v / 100; },
    params: (v) => ({ p: Math.round(v * 0.6) }),
  },
  {
    id: 'critdmg', nameKey: 'talent.critdmg.name', descKey: 'talent.critdmg.desc', step: 25,
    apply: (p, v) => { p.critMul += v / 100; },
  },
  {
    id: 'steal', nameKey: 'talent.steal.name', descKey: 'talent.steal.desc', step: 25,
    apply: (p, v) => { p.lifesteal += Math.round(v / 100 * 5); },
    params: (v) => ({ n: Math.round(v / 100 * 5) }),
  },
  {
    id: 'healhit', nameKey: 'talent.healhit.name', descKey: 'talent.healhit.desc', step: 25,
    apply: (p, v) => { p.healPerHit += v / 100; },
    params: (v) => ({ n: (v / 100).toFixed(2) }),
  },
  {
    id: 'regen', nameKey: 'talent.regen.name', descKey: 'talent.regen.desc', step: 25,
    apply: (p, v) => { p.regenPerFloor += Math.round(v / 100 * 60); },
    params: (v) => ({ n: Math.round(v / 100 * 60) }),
  },
  {
    id: 'thorns', nameKey: 'talent.thorns.name', descKey: 'talent.thorns.desc', step: 25,
    apply: (p, v) => { p.thorns += Math.round(v / 100 * 30); },
    params: (v) => ({ n: Math.round(v / 100 * 30) }),
  },
  {
    id: 'armor', nameKey: 'talent.armor.name', descKey: 'talent.armor.desc', step: 20,
    apply: (p, v) => { p.damageReduction += 0.5 * v / 100; },
    params: (v) => ({ p: Math.round(v * 0.5) }),
  },
  {
    id: 'luck', nameKey: 'talent.luck.name', descKey: 'talent.luck.desc', step: 20,
    apply: (p, v) => { p.dropMul += v / 100; },
  },
  {
    id: 'greed', nameKey: 'talent.greed.name', descKey: 'talent.greed.desc', step: 25,
    apply: (p, v) => { p.scoreMul += v / 100; },
  },
  {
    id: 'magnet', nameKey: 'talent.magnet.name', descKey: 'talent.magnet.desc', step: 25,
    apply: (p, v) => { p.pickupRadius += v / 100 * 220; },
    params: (v) => ({ n: Math.round(v / 100 * 220) }),
  },
  {
    id: 'stable', nameKey: 'talent.stable.name', descKey: 'talent.stable.desc', step: 20,
    apply: (p, v) => { p.knockbackResist += 0.8 * v / 100; },
    params: (v) => ({ p: Math.round(v * 0.8) }),
  },
  {
    id: 'breach', nameKey: 'talent.breach.name', descKey: 'talent.breach.desc', step: 25,
    apply: (p, v) => { p.eliteDmgMul += v / 100; },
  },
  {
    id: 'frost', nameKey: 'talent.frost.name', descKey: 'talent.frost.desc', step: 20,
    apply: (p, v) => { p.slowAmount += 0.6 * v / 100; },
    params: (v) => ({ p: Math.round(v * 0.6) }),
  },
];

export function getTalent(id: string): TalentDef | undefined {
  return TALENTS.find((t) => t.id === id);
}

export function talentName(t: TalentDef): string {
  return tr(t.nameKey);
}

export function talentDesc(t: TalentDef, v: number): string {
  return tr(t.descKey, { v, ...(t.params ? t.params(v) : {}) });
}

/**
 * 随机抽取 n 个不重复天赋。
 * @param exclude 返回 true 的天赋会被排除（例如已叠满 100% 的）
 */
export function rollTalents(
  n: number,
  exclude?: (id: string) => boolean
): TalentDef[] {
  const pool = TALENTS.filter((t) => !(exclude && exclude(t.id)));
  const out: TalentDef[] = [];
  while (out.length < n && pool.length > 0) {
    out.push(pool.splice(Math.floor(Math.random() * pool.length), 1)[0]);
  }
  return out;
}

/** 天赋栏文字（HUD 与天赋面板共用，单一来源） */
export function talentsSummary(talents: Map<string, number>, floor: number = 1): string {
  const held = [...talents.entries()];
  const slots = getTalentSlots(floor);
  const parts = held.map(([id, v]) => {
    const t = getTalent(id);
    return `${t ? talentName(t) : id} ${v}%`;
  });
  while (parts.length < slots) parts.push(tr('common.none'));
  return tr('hud.talents', { n: held.length, max: slots, list: parts.join('  |  ') });
}
